import axios from 'axios';
import { environment } from '../config/environment';

export const apiBaseUrl = environment.apiUrl;

/**
 * Centralized API Client
 * Following the documentation guidelines for API integration
 * Provides consistent API communication with authentication and error handling
 */

// Create axios instance with default configuration
const api = axios.create({
  // Always hit the backend directly to avoid dev proxy issues
  baseURL: environment.apiUrl,
  timeout: environment.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add authentication token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common responses
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Only force logout if the profile endpoint itself returned 401 (token truly invalid)
      if (error.config && error.config.url && error.config.url.includes('/api/auth/profile')) {
        localStorage.removeItem('jwt_token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      return Promise.reject(new Error('Server error occurred'));
    }

    // Handle validation errors
    if (error.response?.status === 422) {
      const validationErrors = error.response.data.errors || {};
      return Promise.reject(
        new Error('Validation failed', { cause: validationErrors })
      );
    }

    return Promise.reject(error);
  }
);

/**
 * API Service Classes
 * Organized by module following the documentation structure
 */

// Authentication API
export const authAPI = {
  login: credentials => api.post('/api/auth/login', credentials),
  publisherLogin: credentials => api.post('/api/auth/publisher/login', credentials),
  register: userData => api.post('/api/auth/register', userData),
  logout: () => api.post('/api/auth/logout'),
  verify: () => api.get('/api/auth/verify'),
  refresh: () => api.post('/api/auth/refresh'),
};

export const notificationAPI = {
  getNotifications: params => api.get('/api/v1/notifications', { params }),
  markRead: id => api.put(`/api/v1/notifications/${id}/read`),
  markAllRead: () => api.put('/api/v1/notifications/read-all'),
};

// User Management API
export const userAPI = {
  getProfile: () => api.get('/api/users/profile'),
  updateProfile: userData => api.put('/api/users/profile', userData),
  getUsers: params => api.get('/api/users', { params }),
  getUser: id => api.get(`/api/users/${id}`),
  createUser: userData => api.post('/api/users', userData),
  updateUser: (id, userData) => api.put(`/api/users/${id}`, userData),
  deleteUser: id => api.delete(`/api/users/${id}`),
  getUserPermissions: userId => api.get(`/api/user/${userId}/permissions`),
  updateUserPermissions: (userId, permissionIds) => api.put(`/api/user/${userId}/permissions`, permissionIds),

  // User roles management
  getUserRoles: userId => api.get(`/api/users/${userId}/roles`),
  assignUserRoles: (userId, roleIds) => api.post(`/api/users/${userId}/roles`, roleIds),
  getGeographyAssignments: userId => api.get(`/api/v1/admin/users/${userId}/geography-assignments`),
  updateGeographyAssignments: (userId, data) => api.put(`/api/v1/admin/users/${userId}/geography-assignments`, data),
};

// Enrollment API
export const enrollmentAPI = {
  getCollectionSummary: params => api.get('/api/enrollment-collection/summary', { params }),
  // Region Management
  getRegions: () => api.get('/api/v1/enrollment/region'),
  getRegion: id => api.get(`/api/v1/enrollment/region/${id}`),
  createRegion: regionData => api.post('/api/v1/enrollment/region', regionData),
  updateRegion: (id, regionData) =>
    api.put(`/api/v1/enrollment/region/${id}`, regionData),
  deleteRegion: id => api.delete(`/api/v1/enrollment/region/${id}`),

  // District Management
  getDistricts: regionId =>
    api.get('/api/v1/enrollment/district', { params: { regionId } }),
  getDistrict: id => api.get(`/api/v1/enrollment/district/${id}`),
  createDistrict: districtData =>
    api.post('/api/v1/enrollment/district', districtData),
  updateDistrict: (id, districtData) =>
    api.put(`/api/v1/enrollment/district/${id}`, districtData),
  deleteDistrict: id => api.delete(`/api/v1/enrollment/district/${id}`),

  // Taluka Management
  getTalukas: districtId =>
    api.get('/api/v1/enrollment/taluka', { params: { districtId } }),
  getTaluka: id => api.get(`/api/v1/enrollment/taluka/${id}`),
  createTaluka: talukaData => api.post('/api/v1/enrollment/taluka', talukaData),
  updateTaluka: (id, talukaData) =>
    api.put(`/api/v1/enrollment/taluka/${id}`, talukaData),
  deleteTaluka: id => api.delete(`/api/v1/enrollment/taluka/${id}`),

  // School Management
  getSchools: talukaId =>
    api.get('/api/v1/enrollment/school', { params: { talukaId } }),
  getSchool: id => api.get(`/api/v1/enrollment/school/${id}`),
  createSchool: schoolData => api.post('/api/v1/enrollment/school', schoolData),
  updateSchool: (id, schoolData) =>
    api.put(`/api/v1/enrollment/school/${id}`, schoolData),
  deleteSchool: id => api.delete(`/api/v1/enrollment/school/${id}`),

  // Student Management
  getStudents: schoolId =>
    api.get('/api/v1/enrollment/student', { params: { schoolId } }),
  getStudent: id => api.get(`/api/v1/enrollment/student/${id}`),
  createStudent: studentData =>
    api.post('/api/v1/enrollment/student', studentData),
  updateStudent: (id, studentData) =>
    api.put(`/api/v1/enrollment/student/${id}`, studentData),
  deleteStudent: id => api.delete(`/api/v1/enrollment/student/${id}`),
};

// RSU Validation API
export const rsuAPI = {
  getValidations: params => api.get('/api/v1/rsu-validation', { params }),
  getValidation: id => api.get(`/api/v1/rsu-validation/${id}`),
  createValidation: validationData =>
    api.post('/api/v1/rsu-validation', validationData),
  updateValidation: (id, validationData) =>
    api.put(`/api/v1/rsu-validation/${id}`, validationData),
  deleteValidation: id => api.delete(`/api/v1/rsu-validation/${id}`),
};

// Control Room Log API
export const controlRoomAPI = {
  getLogs: params => api.get('/api/v1/control-room-log', { params }),
  getLog: id => api.get(`/api/v1/control-room-log/${id}`),
  createLog: logData => api.post('/api/v1/control-room-log', logData),
  updateLog: (id, logData) =>
    api.put(`/api/v1/control-room-log/${id}`, logData),
  deleteLog: id => api.delete(`/api/v1/control-room-log/${id}`),
};

// Role Management API
export const roleAPI = {
  getRoles: () => api.get('/api/v1/roles'),
  getRole: id => api.get(`/api/v1/roles/${id}`),
  createRole: roleData => api.post('/api/v1/roles', roleData),
  updateRole: (id, roleData) => api.put(`/api/v1/roles/${id}`, roleData),
  deleteRole: id => api.delete(`/api/v1/roles/${id}`),
  updateRolePermissions: async (id, permIds) => {
    const current = await api.get(`/api/v1/roles/${id}`);
    const { name, description } = current.data || {};
    return api.put(`/api/v1/roles/${id}`, { name, description, permissionIds: permIds });
  },
};

// Permission Management API
export const permissionAPI = {
  getPermissions: params => api.get('/api/v1/permissions', { params }),
  getPermissionsGrouped: () => api.get('/api/v1/permissions/grouped'),
  getPermission: id => api.get(`/api/v1/permissions/${id}`),
  createPermission: permissionData =>
    api.post('/api/v1/permissions', permissionData),
  updatePermission: (id, permissionData) =>
    api.put(`/api/v1/permissions/${id}`, permissionData),
  deletePermission: id => api.delete(`/api/v1/permissions/${id}`),
};

// Taluka Management API
export const talukaAPI = {
  getAll: () => api.get('/api/v1/taluka'),
  get: id => api.get(`/api/v1/taluka/${id}`),
  create: data => api.post('/api/v1/taluka', data),
  update: (id, data) => api.put(`/api/v1/taluka/${id}`, data),
  delete: id => api.delete(`/api/v1/taluka/${id}`),
};
// CSV helpers
talukaAPI.exportCsv  = () => api.get('/api/v1/taluka/csv/export', { responseType: 'blob' });
talukaAPI.importCsv  = (text) => api.post('/api/v1/taluka/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
talukaAPI.sampleCsv  = () => api.get('/api/v1/taluka/csv/sample', { responseType: 'blob' });
talukaAPI.nextCode   = () => api.get('/api/v1/taluka/next-code');

// District Management API
export const districtAPI = {
  getAll: (talukaId) => api.get('/api/v1/district', { params: talukaId ? { talukaId } : undefined }),
  get: id => api.get(`/api/v1/district/${id}`),
  create: data => api.post('/api/v1/district', data, { headers:{ 'Content-Type':'application/json' }}),
  update: (id, data) => api.put(`/api/v1/district/${id}`, JSON.stringify(data), { headers:{ 'Content-Type':'application/json' }}),
  delete: id => api.delete(`/api/v1/district/${id}`),
};
// CSV helpers
districtAPI.exportCsv = () => api.get('/api/v1/district/csv/export', { responseType: 'blob' });
districtAPI.importCsv = (text) => api.post('/api/v1/district/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
districtAPI.sampleCsv = () => api.get('/api/v1/district/csv/sample', { responseType: 'blob' });
districtAPI.nextCode  = () => api.get('/api/v1/district/next-code');

// School Management API
export const schoolAPI = {
  getAll: () => api.get('/api/v1/school'),
  get: id => api.get(`/api/v1/school/${id}`),
  create: data => api.post('/api/v1/school', data),
  update: (id, data) => api.put(`/api/v1/school/${id}`, data),
  delete: id => api.delete(`/api/v1/school/${id}`),
};

// CSV helpers for schools
schoolAPI.exportCsv = () => api.get('/api/v1/school/csv/export', { responseType: 'blob' });
schoolAPI.importCsv = (text) => api.post('/api/v1/school/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
schoolAPI.sampleCsv = () => api.get('/api/v1/school/csv/sample', { responseType: 'blob' });
schoolAPI.nextCode  = () => api.get('/api/v1/school/next-code');

// Student Management API
export const studentAPI = {
  getAll: () => api.get('/api/v1/student'),
  get: id => api.get(`/api/v1/student/${id}`),
  create: data => api.post('/api/v1/student', data),
  update: (id, data) => api.put(`/api/v1/student/${id}`, data),
  delete: id => api.delete(`/api/v1/student/${id}`),
  // CSV helpers
  downloadSampleCsv: () => api.get('/api/v1/student/csv/sample', { responseType: 'blob' }),
  exportCsv: () => api.get('/api/v1/student/csv/export', { responseType: 'blob' }),
  importCsv: (csvText) => api.post('/api/v1/student/csv/import', csvText, { headers: { 'Content-Type': 'text/plain' } }),
};

// Region Management API
export const regionAPI = {
  getAll: (districtId) => api.get('/api/v1/region', { params: districtId ? { districtId } : undefined }),
  get: id => api.get(`/api/v1/region/${id}`),
  create: data => api.post('/api/v1/region', JSON.stringify(data), { headers:{'Content-Type':'application/json'} }),
  update: (id, data) => api.put(`/api/v1/region/${id}`, JSON.stringify(data), { headers:{'Content-Type':'application/json'} }),
  delete: id => api.delete(`/api/v1/region/${id}`),
};
// CSV helpers
regionAPI.exportCsv = () => api.get('/api/v1/region/csv/export', { responseType: 'blob' });
regionAPI.importCsv = (text) => api.post('/api/v1/region/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
regionAPI.sampleCsv = () => api.get('/api/v1/region/csv/sample', { responseType: 'blob' });
regionAPI.nextCode  = () => api.get('/api/v1/region/next-code');

// User Roles Management API
export const userRolesAPI = {
  getUserRoles: (userId) => api.get(`/api/users/${userId}/roles`),
  assignRoles: (userId, roleIds) => api.post(`/api/users/${userId}/roles`, roleIds),
  removeRoles: (userId, roleIds) => api.delete(`/api/users/${userId}/roles`, { data: roleIds }),
};

// Admin Management API
export const adminAPI = {
  getAdmins: params => api.get('/api/v1/admins', { params }),
  getAdmin: id => api.get(`/api/v1/admins/${id}`),
  createAdmin: adminData => api.post('/api/v1/admins', adminData),
  updateAdmin: (id, adminData) => api.put(`/api/v1/admins/${id}`, adminData),
  deleteAdmin: id => api.delete(`/api/v1/admins/${id}`),
  updateAdminStatus: (id, status) => api.patch(`/api/v1/admins/${id}/status`, { status }),
  getAdminRoles: (adminId) => api.get(`/api/v1/admins/${adminId}/roles`),
  assignRoles: (adminId, roleIds) => api.post(`/api/v1/admins/${adminId}/roles`, roleIds),
  removeRoles: (adminId, roleIds) => api.delete(`/api/v1/admins/${adminId}/roles`, { data: roleIds }),
};

/**
 * Utility functions for API calls
 */

// Generic CRUD operations
export const createCRUD = endpoint => ({
  getAll: params => api.get(endpoint, { params }),
  getById: id => api.get(`${endpoint}/${id}`),
  create: data => api.post(endpoint, data),
  update: (id, data) => api.put(`${endpoint}/${id}`, data),
  delete: id => api.delete(`${endpoint}/${id}`),
});

// Error handling utility
export const handleApiError = error => {
  if (error.response) {
    const { status, data } = error.response;

    switch (status) {
      case 400:
        return `Bad request: ${data.message || 'Invalid data provided'}`;
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'Access denied. You do not have permission for this action.';
      case 404:
        return 'Resource not found.';
      case 422:
        return `Validation error: ${data.message || 'Invalid input data'}`;
      case 500:
        return 'Server error. Please try again later.';
      default:
        return data.message || 'An unexpected error occurred.';
    }
  } else if (error.request) {
    return 'Network error. Please check your connection.';
  } else {
    return error.message || 'An unexpected error occurred.';
  }
};

// Success response utility
export const handleApiSuccess = (
  response,
  message = 'Operation completed successfully'
) => {
  return {
    success: true,
    data: response.data,
    message,
  };
};

export default api;

// Book/Course/Class APIs (declared after createCRUD to avoid temporal dead zone)
export const bookAPI = createCRUD('/api/v1/book');
export const courseAPI = createCRUD('/api/v1/course');
export const classAPI = createCRUD('/api/v1/class');

// CSV helpers for books
bookAPI.exportCsv = () => api.get('/api/v1/book/csv/export', { responseType: 'blob' });
bookAPI.importCsv = (text) => api.post('/api/v1/book/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
bookAPI.sampleCsv = () => api.get('/api/v1/book/csv/sample', { responseType: 'blob' });

// CSV helpers for courses/classes
courseAPI.exportCsv = () => api.get('/api/v1/course/csv/export', { responseType: 'blob' });
courseAPI.importCsv = (text) => api.post('/api/v1/course/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
courseAPI.sampleCsv = () => api.get('/api/v1/course/csv/sample', { responseType: 'blob' });

classAPI.exportCsv = () => api.get('/api/v1/class/csv/export', { responseType: 'blob' });
classAPI.importCsv = (text) => api.post('/api/v1/class/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
classAPI.sampleCsv = () => api.get('/api/v1/class/csv/sample', { responseType: 'blob' });

// Inventory & publisher modules
export const publisherAdminAPI = createCRUD('/api/v1/publisher');

export const warehouseAPI = createCRUD('/api/v1/warehouse');
warehouseAPI.exportCsv = () => api.get('/api/v1/warehouse/csv/export', { responseType: 'blob' });
warehouseAPI.importCsv = (text) => api.post('/api/v1/warehouse/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
warehouseAPI.sampleCsv = () => api.get('/api/v1/warehouse/csv/sample', { responseType: 'blob' });

export const workOrderAPI = {
  getAll: () => api.get('/api/work-order'),
  getMine: () => api.get('/api/work-order/publisher/my'),
  get: id => api.get(`/api/work-order/${id}`),
  create: data => api.post('/api/work-order', data),
  update: (id, data) => api.put(`/api/work-order/${id}`, data),
  delete: id => api.delete(`/api/work-order/${id}`),
  exportCsv: () => api.get('/api/work-order/csv/export'),
  importCsv: text => api.post('/api/work-order/csv/import', text, { headers: { 'Content-Type': 'text/plain' } }),
  sampleCsv: () => api.get('/api/work-order/csv/sample'),
  exists: orderNumber => api.get('/api/work-order/by-number', { params: { orderNumber } }),
};

// Publisher self-service API
export const publisherSelfAPI = {
  getProfile: () => api.get('/api/publisher/me/profile'),
  updateProfile: data => api.put('/api/publisher/me/profile', data),
  getWorkOrders: () => api.get('/api/publisher/me/work-orders'),
  getWorkOrder: id => api.get(`/api/publisher/me/work-orders/${id}`),
  getSummary: () => api.get('/api/publisher/me/dashboard-summary'),
  getTenders: () => api.get('/api/publisher/me/tenders'),
};

export const publisherTenderAPI = { list: () => api.get('/api/publisher/me/tenders') };

export const publisherDispatchAPI = { list: () => api.get('/api/publisher/me/dispatch-notes') };

export const publisherTicketsAPI = { list: () => api.get('/api/publisher/me/tickets') };

// Backward compatibility: admin CRUD remains publisherAPI
export const publisherAPI = publisherAdminAPI;

// Procurement APIs
export const tenderAPI = createCRUD('/api/tenders');
tenderAPI.publish = id => api.put(`/api/tenders/${id}/publish`);
tenderAPI.close = id => api.put(`/api/tenders/${id}/close`);
tenderAPI.assignPublisher = (id, publisherId) => api.put(`/api/tenders/${id}/assign-publisher`, { publisherId });
tenderAPI.generateWorkOrder = (id, payload = {}) => api.post(`/api/tenders/${id}/generate-work-order`, payload);

export const berAPI = createCRUD('/api/ber');
berAPI.upload = (tenderId, file) => {
  const form = new FormData();
  form.append('tenderId', tenderId);
  form.append('file', file);
  return api.post('/api/ber/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
berAPI.authorize = id => api.put(`/api/ber/${id}/authorize`);

export const bidderAPI = createCRUD('/api/qualified-bidders');
bidderAPI.validate = id => api.put(`/api/qualified-bidders/${id}/validate`);
bidderAPI.disqualify = id => api.put(`/api/qualified-bidders/${id}/disqualify`);

// Tender files API
export const tenderFileAPI={
  list:(id)=>api.get(`/api/tenders/${id}/files`),
  upload:(tenderId,name,file,onProgress)=>{
    const f=new FormData();
    f.append('name',name);
    f.append('file',file);
    const cfg={headers:{'Content-Type':'multipart/form-data'}};
    if(onProgress){cfg.onUploadProgress=e=>onProgress(Math.round((e.loaded*100)/e.total));}
    return api.post(`/api/tenders/${tenderId}/files`,f,cfg);
  },
  delete:(id)=>api.delete(`/api/tenders/files/${id}`)
};

// Package Management API
export const packageAPI = {
  getAll: () => api.get('/api/v1/package'),
  get: id => api.get(`/api/v1/package/${id}`),
  create: data => api.post('/api/v1/package', data),
  update: (id, data) => api.put(`/api/v1/package/${id}`, data),
  delete: id => api.delete(`/api/v1/package/${id}`),
};
packageAPI.exportCsv = () => api.get('/api/v1/package/csv/export', { responseType: 'blob' });
packageAPI.importCsv = (text) => api.post('/api/v1/package/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
packageAPI.sampleCsv = () => api.get('/api/v1/package/csv/sample', { responseType: 'blob' });

export const projectAPI = createCRUD('/api/projects');
projectAPI.exportCsv  = () => api.get('/api/projects/csv/export', { responseType: 'blob' });
projectAPI.importCsv  = (text) => api.post('/api/projects/csv/import', text, { headers: { 'Content-Type': 'text/plain' } });
projectAPI.sampleCsv  = () => api.get('/api/projects/csv/sample', { responseType: 'blob' });

// Reports API
export const reportAPI={
  workOrderSummary:(params)=>api.get('/api/reports/work-orders/summary',{params}),
  workOrderTimeSeries:(params)=>api.get('/api/reports/work-orders/timeseries',{params}),
  getpublisherWOSummary:(vid,params)=>api.get(`/api/reports/publisher/${vid}/work-orders/summary`,{params}),
  getpublisherWOTimeseries:(vid,params)=>api.get(`/api/reports/publisher/${vid}/work-orders/timeseries`,{params}),
  exportCsv:(type)=>api.get(`/api/v1/reports/export/${type}`,{responseType:'blob'}),
};

// Inventory API
export const inventoryAPI = {
  // Dispatch Notes
  createDispatchNote: data => api.post('/api/v1/inventory/dispatch-notes', data),
  getDispatchNotes: params => api.get('/api/v1/inventory/dispatch-notes', { params }),
  getDispatchNote: id => api.get(`/api/v1/inventory/dispatch-notes/${id}`),
  // Gate Scan
  gateScan: payload => api.post('/api/v1/inventory/gate-scan', payload),
  // Receipt Notes
  approveReceipt: id => api.post(`/api/v1/inventory/receipt-notes/${id}/approve`),
};

// Enhanced dispatch helpers
export const dispatchAPI = {
  gateScan: (id, qr, overrideReason)=> api.post('/api/v1/inventory/dispatch-notes/gate-scan', { id, qr, overrideReason }),
  provisional: id=> api.post(`/api/v1/inventory/dispatch-notes/${id}/provisional`),
  approve: id=> api.post(`/api/v1/inventory/dispatch-notes/${id}/approve`),
  qcAction: (id, data)=> api.post(`/api/v1/inventory/dispatch-notes/${id}/qc-action`, data),
  nextRef: woNo => api.get('/api/v1/inventory/dispatch-notes/next-ref', { params:{ workOrderNo: woNo } }),
};

export const distributionAPI = {
  stockSummary: () => api.get('/api/v1/distribution/stock-summary'),
  requisitions: {
    list: () => api.get('/api/v1/distribution/requisitions'),
    create: data => api.post('/api/v1/distribution/requisitions', data),
    approve: (id, data = {}) => api.post(`/api/v1/distribution/requisitions/${id}/approve`, data),
  },
  handovers: {
    list: () => api.get('/api/v1/distribution/handovers'),
    create: data => api.post('/api/v1/distribution/handovers', data),
    acknowledge: (id, qr) => api.post(`/api/v1/distribution/handovers/${id}/acknowledge`, { qr }),
  },
};

export const executiveAPI = {
  ministerSummary: () => api.get('/api/v1/executive/minister-summary'),
};

export const operationalEventAPI = {
  list: params => api.get('/api/v1/events/operational', { params }),
  timeline: (entityType, entityId) => api.get(`/api/v1/events/operational/${entityType}/${entityId}`),
};

// Finance APIs
export const financeAPI = {
  // Invoices
  invoices: createCRUD('/api/v1/finance/invoices'),
  // Bills lifecycle
  bills: createCRUD('/api/v1/finance/bills'),
  receiveBill: id => api.post(`/api/v1/finance/bills/${id}/receive`),
  processBill: id => api.post(`/api/v1/finance/bills/${id}/process`),
  approveBill: id => api.post(`/api/v1/finance/bills/${id}/approve`),
  // Vouchers
  vouchers: {
    ...createCRUD('/api/v1/finance/vouchers'),
    approve: id => api.put(`/api/v1/finance/vouchers/${id}/approve`),
    paid: id => api.put(`/api/v1/finance/vouchers/${id}/paid`),
  },
  expenses: createCRUD('/api/v1/finance/expenses'),
  generateVoucher: id => api.post(`/api/v1/finance/bills/${id}/voucher`),
  payVoucher: id => api.post(`/api/v1/finance/vouchers/${id}/pay`),
  // Dashboard summary
  summary: () => api.get('/api/v1/finance/summary'),
  generateFromDispatch: payload => api.post('/api/v1/finance/invoices/generate', payload),
};

export const auditAPI = {
  getLogs: params => api.get('/api/audit-logs', { params }),
};

// Database Backups API
export const backupAPI = {
  getAll: () => api.get('/api/v1/backups'),
  generate: () => api.post('/api/v1/backups/generate'),
  delete: filename => api.delete(`/api/v1/backups/${filename}`),
  downloadUrl: filename => `${environment.apiUrl}/api/v1/backups/${filename}/download`
};

// Global Unified Search API
export const searchAPI = {
  globalSearch: (q, category) => api.get('/api/v1/search', { params: { q, category } })
};

export const procurementAPI = {
  summary: () => api.get('/api/procurement/summary'),
  enrollmentAnalytics: params => api.get('/api/procurement/enrollment-analytics', { params }),
  assignPublisher: (tenderId, publisherId) => api.put(`/api/tenders/${tenderId}/assign-publisher`, { publisherId }),
  generateWorkOrder: (tenderId, payload = {}) => api.post(`/api/tenders/${tenderId}/generate-work-order`, payload),
};
